import React, { useState, useEffect, useRef } from 'react';
import { SearchFilters } from '../types';
import { getCompanyNameSuggestions, getJobTitleSuggestions } from '../services/jobService';
import { logUserInteraction} from '../lib/logger';

interface SearchFormProps {
  onSearch: (filters: SearchFilters) => void;
}

// Debounce hook for search optimization
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SearchForm: React.FC<SearchFormProps> = ({ onSearch }) => {
  const [filters, setFilters] = useState<SearchFilters>({
    companyName: '',
    jobTitle: '',
    location: '',
    year: 2025,
  });
  
  // Company suggestions state
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(-1);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [inputCompanyValue, setInputCompanyValue] = useState('');
  const [inputJobTitleValue, setInputJobTitleValue] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  const companySuggestionsRef = useRef<HTMLDivElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);
  
  // Job title suggestions state
  const [jobTitles, setJobTitles] = useState<string[]>([]);
  const [showJobTitleSuggestions, setShowJobTitleSuggestions] = useState(false);
  const [selectedJobTitleIndex, setSelectedJobTitleIndex] = useState(-1);
  const [isLoadingJobTitles, setIsLoadingJobTitles] = useState(false);
  const jobTitleSuggestionsRef = useRef<HTMLDivElement>(null);
  const jobTitleInputRef = useRef<HTMLInputElement>(null);

  // Debounced search terms for API calls
  const debouncedCompanySearch = useDebounce(filters.companyName, 300);
  const debouncedJobTitleSearch = useDebounce(filters.jobTitle, 300);

  // Search for company names when debounced search term changes
  useEffect(() => {
    const searchCompanies = async () => {
      if (debouncedCompanySearch.length >= 2 && !isSelecting) {
        setIsLoadingCompanies(true);
        try {
          const suggestions = await getCompanyNameSuggestions(debouncedCompanySearch, 10);
          setCompanyNames(suggestions);
          // Don't show suggestions if the current input exactly matches one of the suggestions
          // This prevents showing dropdown after a selection is made
          const exactMatch = suggestions.some(suggestion => 
            suggestion.toLowerCase() === debouncedCompanySearch.toLowerCase()
          );
          setShowCompanySuggestions(suggestions.length > 0 && !exactMatch);
        } catch (error) {
          console.error('Error searching companies:', error);
          setCompanyNames([]);
        } finally {
          setIsLoadingCompanies(false);
        }
      } else {
        setShowCompanySuggestions(false);
      }
    };

    if (isSelecting) {
      // Reset the selecting flag after the effect runs
      setIsSelecting(false);
      return;
    }

    searchCompanies();
  }, [debouncedCompanySearch, isSelecting]);

  // Search for job titles when debounced search term changes
  useEffect(() => {
    const searchJobTitles = async () => {
      if (debouncedJobTitleSearch.length >= 2) {
        setIsLoadingJobTitles(true);
        try {
          const suggestions = await getJobTitleSuggestions(debouncedJobTitleSearch, 10);
          setJobTitles(suggestions);
          // Don't show suggestions if the current input exactly matches one of the suggestions
          // This prevents showing dropdown after a selection is made
          const exactMatch = suggestions.some(suggestion => 
            suggestion.toLowerCase() === debouncedJobTitleSearch.toLowerCase()
          );
          setShowJobTitleSuggestions(suggestions.length > 0 && !exactMatch);
        } catch (error) {
          console.error('Error searching job titles:', error);
          setJobTitles([]);
        } finally {
          setIsLoadingJobTitles(false);
        }
      } else {
        setShowJobTitleSuggestions(false);
      }
    };

    searchJobTitles();
  }, [debouncedJobTitleSearch]);
  
  // Handle clicks outside company suggestions dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companySuggestionsRef.current && 
        !companySuggestionsRef.current.contains(event.target as Node) &&
        !companyInputRef.current?.contains(event.target as Node)
      ) {
        setShowCompanySuggestions(false);
        setSelectedCompanyIndex(-1);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle clicks outside job title suggestions dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        jobTitleSuggestionsRef.current && 
        !jobTitleSuggestionsRef.current.contains(event.target as Node) &&
        !jobTitleInputRef.current?.contains(event.target as Node)
      ) {
        setShowJobTitleSuggestions(false);
        setSelectedJobTitleIndex(-1);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset selection indices when typing
    if (name === 'companyName') {
      setInputCompanyValue(value);
      setSelectedCompanyIndex(-1);
    }
    if (name === 'jobTitle') {
      setInputJobTitleValue(value);
      setSelectedJobTitleIndex(-1);
    }
  };
  
  // Helper function to highlight matching text
  const highlightMatch = (text: string, searchTerm: string): JSX.Element => {
    if (!searchTerm) return <span>{text}</span>;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <span>
        {parts.map((part, index) => 
          regex.test(part) ? (
            <span key={index} className="font-semibold text-[#4F46E5]">{part}</span>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };
  
  const handleCompanySuggestionClick = (suggestion: string) => {
    setIsSelecting(true);
    setFilters(prev => ({
      ...prev,
      companyName: suggestion
    }));
    setInputCompanyValue(suggestion);
    setShowCompanySuggestions(false);
    setSelectedCompanyIndex(-1);
  };
  
  const handleJobTitleSuggestionClick = (suggestion: string) => {
    setFilters(prev => ({
      ...prev,
      jobTitle: suggestion
    }));
    setInputJobTitleValue(suggestion);
    setShowJobTitleSuggestions(false);
    setSelectedJobTitleIndex(-1);
  };

  // Keyboard navigation for company suggestions
  const handleCompanyKeyDown = (e: React.KeyboardEvent) => {
    if (!showCompanySuggestions || companyNames.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCompanyIndex(prev => 
          prev < companyNames.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCompanyIndex(prev => 
          prev > 0 ? prev - 1 : companyNames.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCompanyIndex >= 0) {
          handleCompanySuggestionClick(companyNames[selectedCompanyIndex]);
        }
        break;
      case 'Escape':
        setShowCompanySuggestions(false);
        setSelectedCompanyIndex(-1);
        setInputCompanyValue('');
        break;
    }
  };

  // Keyboard navigation for job title suggestions
  const handleJobTitleKeyDown = (e: React.KeyboardEvent) => {
    if (!showJobTitleSuggestions || jobTitles.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedJobTitleIndex(prev => 
          prev < jobTitles.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedJobTitleIndex(prev => 
          prev > 0 ? prev - 1 : jobTitles.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedJobTitleIndex >= 0) {
          handleJobTitleSuggestionClick(jobTitles[selectedJobTitleIndex]);
        }
        break;
      case 'Escape':
        setShowJobTitleSuggestions(false);
        setSelectedJobTitleIndex(-1);
        setInputJobTitleValue('');
        break;
    }
  };

  // Handle select change for dropdown fields
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFilters(prev => ({
      ...prev,
      [name]: name === 'year' ? parseInt(value, 10) : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure filters are updated with current input values
    const combinedFilters = {
      ...filters,
      companyName: inputCompanyValue || filters.companyName,
      jobTitle: inputJobTitleValue || filters.jobTitle,
    };
    
    // Log the search action
    logUserInteraction('search_submit', 'search_form', {
      hasCompanyName: !!combinedFilters.companyName,
      hasJobTitle: !!combinedFilters.jobTitle,
      hasLocation: !!combinedFilters.location,
      year: combinedFilters.year,
      searchTermsLength: {
        companyName: combinedFilters.companyName?.length || 0,
        jobTitle: combinedFilters.jobTitle?.length || 0,
        location: combinedFilters.location?.length || 0,
      }
    });
    
    onSearch(combinedFilters);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-4 md:p-8 rounded-lg shadow-sm border border-[#E5E7EB]">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="relative">
          <label htmlFor="companyName" className="flex items-center text-sm font-medium text-[#4B5563] mb-2">
            <svg className="w-4 h-4 mr-2 text-[#4F46E5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Company
          </label>
          <div className="relative">
            <input
              ref={companyInputRef}
              type="text"
              id="companyName"
              name="companyName"
              value={inputCompanyValue || filters.companyName}
              onChange={handleInputChange}
              onKeyDown={handleCompanyKeyDown}
             // onFocus={handleCompanyFocus}
              className="w-full p-3 pr-10 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent transition-all duration-200 ease-in-out outline-none bg-[#F9FAFB]"
              placeholder="E.g. Google"
              autoComplete="off"
            />
            {isLoadingCompanies && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4F46E5]"></div>
              </div>
            )}
          </div>
          {showCompanySuggestions && companyNames.length > 0 && (
            <div 
              ref={companySuggestionsRef}
              className="absolute z-20 w-full mt-1 bg-white shadow-lg rounded-md border border-[#E5E7EB] max-h-60 overflow-y-auto"
            >
              {companyNames.map((suggestion, index) => (
                <div
                  key={index}
                  className={`p-3 cursor-pointer text-[#4B5563] transition-colors duration-150 border-l-4 ${
                    index === selectedCompanyIndex 
                      ? 'bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]' 
                      : 'hover:bg-[#F9FAFB] border-transparent'
                  }`}
                  onClick={() => handleCompanySuggestionClick(suggestion)}
                  onMouseEnter={() => setSelectedCompanyIndex(index)}
                >
                  {highlightMatch(suggestion, filters.companyName || '')}
                </div>
              ))}
              {companyNames.length === 10 && filters.companyName.length >= 2 && (
                <div className="p-2 text-xs text-[#6B7280] text-center border-t border-[#E5E7EB]">
                  Showing top 10 results. Type more to refine.
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="relative">
          <label htmlFor="jobTitle" className="flex items-center text-sm font-medium text-[#4B5563] mb-2">
            <svg className="w-4 h-4 mr-2 text-[#4F46E5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 6V9a2 2 0 00-2-2H8a2 2 0 00-2 2v7a2 2 0 002 2h8a2 2 0 002-2z" />
            </svg>
            Job Title
          </label>
          <div className="relative">
            <input
              ref={jobTitleInputRef}
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={inputJobTitleValue || filters.jobTitle}
              onChange={handleInputChange}
              onKeyDown={handleJobTitleKeyDown}
              className="w-full p-3 pr-10 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent transition-all duration-200 ease-in-out outline-none bg-[#F9FAFB]"
              placeholder="E.g. Software Engineer"
              autoComplete="off"
            />
            {isLoadingJobTitles && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4F46E5]"></div>
              </div>
            )}
          </div>
          {showJobTitleSuggestions && jobTitles.length > 0 && (
            <div 
              ref={jobTitleSuggestionsRef}
              className="absolute z-20 w-full mt-1 bg-white shadow-lg rounded-md border border-[#E5E7EB] max-h-60 overflow-y-auto"
            >
              {jobTitles.map((suggestion, index) => (
                <div
                  key={index}
                  className={`p-3 cursor-pointer text-[#4B5563] transition-colors duration-150 border-l-4 ${
                    index === selectedJobTitleIndex 
                      ? 'bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]' 
                      : 'hover:bg-[#F9FAFB] border-transparent'
                  }`}
                  onClick={() => handleJobTitleSuggestionClick(suggestion)}
                  onMouseEnter={() => setSelectedJobTitleIndex(index)}
                >
                  {highlightMatch(suggestion, filters.jobTitle || '')}
                </div>
              ))}
              {jobTitles.length === 10 && filters.jobTitle.length >= 2 && (
                <div className="p-2 text-xs text-[#6B7280] text-center border-t border-[#E5E7EB]">
                  Showing top 10 results. Type more to refine.
                </div>
              )}
            </div>
          )}
        </div>
        
        <div>
          <label htmlFor="location" className="flex items-center text-sm font-medium text-[#4B5563] mb-2">
            <svg className="w-4 h-4 mr-2 text-[#4F46E5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Location
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={filters.location}
            onChange={handleInputChange}
            className="w-full p-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent transition-all duration-200 ease-in-out outline-none bg-[#F9FAFB]"
            placeholder="E.g. San Francisco"
          />
        </div>
        
        <div>
          <label htmlFor="year" className="flex items-center text-sm font-medium text-[#4B5563] mb-2">
            <svg className="w-4 h-4 mr-2 text-[#4F46E5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Year
          </label>
          <select
            id="year"
            name="year"
            value={filters.year}
            onChange={handleSelectChange}
            className="w-full p-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent transition-all duration-200 ease-in-out outline-none bg-[#F9FAFB] appearance-none"
            style={{ backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%236B7280\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><polyline points=\"6 9 12 15 18 9\"></polyline></svg>')", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1em' }}
          >
            <option value={2025}>2025</option>
            <option value={2024}>2024</option>
            <option value={2023}>2023</option>
          </select>
        </div>
      </div>
      
      <div className="flex justify-center md:justify-end">
      <button
        type="submit"
          className="px-6 py-3 bg-[#4F46E5] text-white font-medium rounded-md hover:bg-[#4338CA] focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2 transition-colors duration-200 ease-in-out"
      >
          Search
      </button>
      </div>
    </form>
  );
};

export default SearchForm; 